import { Inject, Injectable } from '@angular/core';
import { IShopConfig, SHOP_CONFIG } from '@x/ecommerce-shop/config/shop.config';
import {
  IShopGeoRegionDetail,
  IShopOrder,
  IShopProductItem,
  IShopProductVariant,
} from '@x/ecommerce/shop-client';
import { ShopProductUnavailableReason } from '@x/schemas/ecommerce';
import { DateTime } from 'luxon';
import {
  IAvailabilityReason,
  IVariantAvailability,
} from '../components/variant-availability/variant-availability';

@Injectable()
export class AvailabilityService {
  getVariantAvailability(
    variant: IShopProductVariant,
    order: IShopOrder | null,
    nextAvailableDelayInDays: number = 3,
  ): IVariantAvailability {
    const quantity =
      order?.items?.reduce((n, i) => n + (i.variantId === variant.id ? i.quantity : 0), 0) ?? 0;

    const bagQuantity = order?.items?.find((item) => item.variantId === variant.id)?.quantity || 0;

    const currentRegion = order?.shippingAddressRegion;

    return {
      ...this.getAvailability(variant, currentRegion, nextAvailableDelayInDays),
      variant_id: variant.id,
      variantAvailableRegions: variant.availableRegions,
      showDeliveryRegionBadges: this.shopConfig.features.showDeliveryRegionBadges,
      quantity,
      bagQuantity,
      price: variant.price,
    };
  }

  getProductVariantAvailability(
    product: IShopProductItem,
    order: IShopOrder | null,
    // region: IShopGeoRegionDetail | null,
  ): IVariantAvailability {
    const variantsAvailability = product.variants.map((variant) => {
      return this.getVariantAvailability(variant, order);
    });

    let variantAvailability = variantsAvailability[0];

    if (product.variation === 'OPTION') {
      const sortByCheapest = [...variantsAvailability].sort(
        (a, b) => a.price.amount - b.price.amount,
      );

      const hasAvailableVariant = sortByCheapest.find((v) => v.status !== 'UNAVAILABLE');

      variantAvailability = hasAvailableVariant ? hasAvailableVariant : sortByCheapest[0];
    }

    return variantAvailability;
  }

  private getUnavailableReason(
    reasonCode?: ShopProductUnavailableReason | undefined | null,
  ): IAvailabilityReason {
    switch (reasonCode) {
      case ShopProductUnavailableReason.Unlisted:
      case ShopProductUnavailableReason.UnlistedRegion:
        return {
          label: 'Unavailable',
          description: 'Product not available in your delivery area',
        };
      case ShopProductUnavailableReason.NoShippingMethod:
      case ShopProductUnavailableReason.NoShippingSlot:
      case ShopProductUnavailableReason.NoSlotCapacity:
      case ShopProductUnavailableReason.NoStock:
        return { label: 'Out of Stock' };
    }

    return { label: 'Unavailable', description: 'Product not currently available' };
  }

  private getAvailability(
    variant: IShopProductVariant,
    currentRegion: IShopGeoRegionDetail | undefined | null,
    nextAvailableDelayInDays: number,
  ): Pick<IVariantAvailability, 'status' | 'reason' | 'nextAvailableDate'> {
    if (!variant?.available) {
      return {
        status: 'UNAVAILABLE',
        reason: this.getUnavailableReason(),
      };
    }

    if (currentRegion) {
      const variantRegionMatch = variant.regions?.find((vr) => vr.region?.id === currentRegion?.id);

      if (variantRegionMatch?.isAvailable) {
        const daysFromNow = DateTime.now().plus({ days: nextAvailableDelayInDays });
        const nextAvailableDate = DateTime.fromISO(variantRegionMatch.nextAvailableDate);
        const showDate = nextAvailableDate > daysFromNow;

        return {
          status: 'AVAILABLE',
          ...(showDate && { nextAvailableDate: variantRegionMatch.nextAvailableDate }),
        };
      }

      return {
        status: 'UNAVAILABLE',
        reason: this.getUnavailableReason(
          variantRegionMatch?.unavailableReason ?? ShopProductUnavailableReason.Unlisted,
        ),
      };
    }

    if (variant.availableRegions.length > 0) {
      return { status: 'LIMITED' };
    }

    return { status: 'AVAILABLE' };
  }

  constructor(@Inject(SHOP_CONFIG) private shopConfig: IShopConfig) {}
}
